import React, { useMemo } from "react";
import { selectAppDecimal } from "state/app/selector";
import styled from "styled-components";
import { isNumeric, round } from "@ls/js-utils";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

const Root = styled.div`
  background: #c0c0c0;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 10px;
`;
const Label = styled.span`
  color: #1b1b1b;
  font-size: 12px;
  font-weight: 600;
`;
const Value = styled.span`
  color: var(--ls-color-1);
  font-size: 13px;
  font-weight: bold;
  margin: 0 2px;
`;
const AggStats = styled.span`
  margin: 0 0 0 auto;
`;
const Stat = styled.span`
  margin: 0 5px;
`;

const StatusBar = ({ selectedCellRanges, rowNodesAfterFilter, grid }) => {
  const decimalValue = useSelector(selectAppDecimal);

  const { count, min, max, sum, avg } = useMemo(() => {
    let count = 0,
      min = Number.MAX_SAFE_INTEGER,
      max = Number.MIN_SAFE_INTEGER,
      sum = 0,
      allNumeric = true;
    selectedCellRanges.forEach((cellRange) => {
      const { startRow, endRow, columns } = cellRange;
      let startIndex;
      let endIndex;
      if (startRow.rowIndex > endRow.rowIndex) {
        startIndex = endRow.rowIndex;
        endIndex = startRow.rowIndex;
      } else {
        startIndex = startRow.rowIndex;
        endIndex = endRow.rowIndex;
      }
      for (let rowIndex = startIndex; rowIndex <= endIndex; rowIndex++) {
        // eslint-disable-next-line no-loop-func
        columns.forEach(({ colId }) => {
          const value = grid.api.getValue(
            colId,
            grid.api.getDisplayedRowAtIndex(rowIndex)
          );
          count++;
          if (allNumeric && isNumeric(value)) {
            if (value > max) {
              max = value;
            }
            if (value < min) {
              min = value;
            }
            sum += value;
          } else if (allNumeric) {
            allNumeric = false;
          }
        });
      }
    });
    if (count > 1) {
      if (allNumeric) {
        return {
          count: count,
          min: min,
          max: max,
          sum: sum,
          avg: sum / count,
        };
      } else {
        return {
          count,
          min: null,
          max: null,
          sum: null,
          avg: null,
        };
      }
    } else {
      return {
        count: null,
        min: null,
        max: null,
        sum: null,
        avg: null,
      };
    }
  }, [selectedCellRanges, grid]);

  const accCount = useMemo(() => {
    return rowNodesAfterFilter.filter((row) => !row.data.isBenchmark).length;
  }, [rowNodesAfterFilter]);

  return (
    <Root>
      <span>
        <Label>Account Count:</Label>
        <Value>{accCount}</Value>
      </span>
      {count !== null && (
        <AggStats>
          <Stat>
            <Label>Count:</Label>
            <Value>{count}</Value>
          </Stat>
          {avg !== null && (
            <>
              <Stat>
                <Label>Average:</Label>
                <Value>{round(avg, decimalValue)}</Value>
              </Stat>
              <Stat>
                <Label>Min:</Label>
                <Value>{round(min, decimalValue)}</Value>
              </Stat>
              <Stat>
                <Label>Max:</Label>
                <Value>{round(max, decimalValue)}</Value>
              </Stat>
              <Stat>
                <Label>Sum:</Label>
                <Value>{round(sum, decimalValue)}</Value>
              </Stat>
            </>
          )}
        </AggStats>
      )}
    </Root>
  );
};

StatusBar.propTypes = {
  selectedCellRanges: PropTypes.array,
  rowNodesAfterFilter: PropTypes.array,
  grid: PropTypes.object,
};

export default StatusBar;
